import { useEffect, useId, useRef, useState } from "react";

import { IgTypography } from "@intelligems/ig-theme";
import { ButtonBase, Link, Popover, Stack, useTheme } from "@mui/material";
import { EXPERIMENT_ROUTES, PERSONALIZATION_ROUTES } from "@src/routes/routes";
import { Link as RouterLink } from "react-router-dom";

import type { RuleListUsage } from "./ruleListRows";

interface UsedInProps {
  usages: readonly RuleListUsage[] | null;
  /** Show every usage inline when the surrounding header has room for them. */
  showAll?: boolean;
}

/**
 * The two experience categories are served by different detail routes, so the
 * link is built from the category the assignment reports rather than guessed.
 */
const experienceHref = ({
  id,
  category,
}: NonNullable<RuleListUsage["experience"]>) =>
  category === "personalization"
    ? `${PERSONALIZATION_ROUTES.Personalization}/${id}`
    : `${EXPERIMENT_ROUTES.Experiment}/${id}`;

/** One name in the popover list; the link and the plain name must match. */
const nameSx = {
  fontSize: 12,
  lineHeight: 1.5,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

/** Grace periods that let the pointer cross the gap into the popover. */
const OPEN_DELAY_MS = 90;
const CLOSE_DELAY_MS = 160;

/**
 * "Used in" answers "which placements", not "how many", so the inline form
 * names the first two and clips the rest into a "+n more" tail. It stays one
 * line high so row heights never move; the full list lives in the popover.
 */
export const UsedIn = ({ usages, showAll = false }: UsedInProps) => {
  const theme = useTheme();
  const titleId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => clear, []);

  const openSoon = (element: HTMLElement) => {
    clear();
    timer.current = setTimeout(() => setAnchorEl(element), OPEN_DELAY_MS);
  };
  const closeSoon = () => {
    clear();
    timer.current = setTimeout(() => setAnchorEl(null), CLOSE_DELAY_MS);
  };

  if (usages === null || usages.length === 0) {
    return (
      <IgTypography
        color="text.tertiary"
        sx={{ fontSize: showAll ? 14 : 12.5 }}
      >
        {usages === null ? "Unavailable" : "Not used yet"}
      </IgTypography>
    );
  }

  const visibleNames = (showAll ? usages : usages.slice(0, 2)).map(
    (usage) => usage.name
  );
  const remaining = usages.length - visibleNames.length;
  // The tail is tinted, so it is its own element; that would otherwise fuse
  // into the name without a separator, hence the explicit label.
  const summary = `${visibleNames.join(", ")}${remaining > 0 ? ` +${remaining} more` : ""}`;

  return (
    <>
      <ButtonBase
        aria-label={summary}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchorEl)}
        onClick={(event) => {
          event.stopPropagation();
          clear();
          setAnchorEl(event.currentTarget);
        }}
        onMouseEnter={(event) => openSoon(event.currentTarget)}
        onMouseLeave={closeSoon}
        onFocus={(event) => {
          clear();
          setAnchorEl(event.currentTarget);
        }}
        sx={{
          color: theme.palette.text.secondary,
          display: "block",
          fontSize: showAll ? 14 : 12.5,
          lineHeight: showAll ? 1.5 : 1.35,
          maxWidth: showAll ? "none" : 260,
          overflow: "hidden",
          textAlign: "left",
          textOverflow: "ellipsis",
          transition: theme.transitions.create("color"),
          whiteSpace: "nowrap",
          width: showAll ? "auto" : "100%",
          "&:hover, &:focus-visible": {
            color: theme.palette.text.primary,
          },
        }}
      >
        {visibleNames.join(", ")}
        {remaining > 0 ? (
          <IgTypography
            component="span"
            color="text.tertiary"
            sx={{ fontSize: "inherit" }}
          >
            {` +${remaining} more`}
          </IgTypography>
        ) : null}
      </ButtonBase>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          clear();
          setAnchorEl(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableAutoFocus
        disableRestoreFocus
        // The pointer has to be able to reach the list, so the backdrop that
        // Popover normally lays over the page must not eat mouse events.
        slotProps={{
          root: { sx: { pointerEvents: "none" } },
          paper: {
            role: "dialog",
            "aria-labelledby": titleId,
            onMouseEnter: clear,
            onMouseLeave: closeSoon,
            onClick: (event) => event.stopPropagation(),
            // Popover's paper scrolls by default. The card owns its height so
            // the header stays visible and only the list inside it scrolls.
            sx: {
              border: `1px solid ${theme.igBorders.core.default}`,
              borderRadius: "10px",
              boxShadow: theme.igShadows.low,
              display: "flex",
              flexDirection: "column",
              maxHeight: 320,
              mt: 0.75,
              overflow: "hidden",
              pointerEvents: "auto",
              width: 268,
            },
          },
        }}
      >
        <IgTypography
          id={titleId}
          color="text.placeholder"
          sx={{
            borderBottom: `1px solid ${theme.igBorders.core.disabled}`,
            flexShrink: 0,
            fontSize: 10,
            fontWeight: 600,
            lineHeight: 1.5,
            px: 3,
            py: 2,
          }}
        >
          USED IN {usages.length}{" "}
          {usages.length === 1 ? "EXPERIENCE" : "EXPERIENCES"}
        </IgTypography>
        <Stack
          component="ul"
          sx={{
            listStyle: "none",
            m: 0,
            // minHeight lets this be the flex item that shrinks, so the header
            // keeps its height and only the names scroll.
            minHeight: 0,
            overflowX: "hidden",
            overflowY: "auto",
            py: 1,
          }}
        >
          {usages.map((usage) => (
            <Stack
              component="li"
              key={usage.assignmentId}
              sx={{ px: 3, py: 1.5 }}
            >
              {usage.experience ? (
                <Link
                  component={RouterLink}
                  to={experienceHref(usage.experience)}
                  color="text.primary"
                  underline="hover"
                  sx={{ ...nameSx, display: "block" }}
                >
                  {usage.name}
                </Link>
              ) : (
                <IgTypography color="text.primary" sx={nameSx}>
                  {usage.name}
                </IgTypography>
              )}
              {usage.detail ? (
                <IgTypography
                  color="text.tertiary"
                  sx={{ fontSize: 11, lineHeight: 1.5 }}
                >
                  {usage.detail}
                </IgTypography>
              ) : null}
            </Stack>
          ))}
        </Stack>
      </Popover>
    </>
  );
};
